import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { RefreshButton } from '../hooks/useRefreshTrigger';

// ─────────────────────────────────────────────────────────────────────────────
// Data Sweep — duplicate supplier invoice references on purchase orders
//
// Lists live POs that duplicate an invoice already received against another PO.
// Voiding is done by hand in Cin7; this page is the worklist, not the actioner.
//
// Findings are computed live by get_duplicate_po_findings(), so once a PO is
// voided in Cin7 the row leaves this list by itself at the next PO replication.
// "Mark Fixed" exists for the two cases that does not cover: clearing a row
// immediately rather than waiting on replication, and permanently silencing a
// false positive. It is keyed on the open PO's order_id, so marking one fixed
// can never hide a later duplicate against the same invoice — that would be a
// different PO.
// ─────────────────────────────────────────────────────────────────────────────

const CHECK_KEY = 'duplicate_po_invoice_ref';

const fmtMoney = (n) =>
  n === null || n === undefined
    ? '—'
    : Number(n).toLocaleString('en-NZ', { style: 'currency', currency: 'NZD' });

const fmtNZT = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

// ── Confirm modal ────────────────────────────────────────────────────────────
const FixModal = ({ row, initials, onConfirm, onCancel, saving }) => {
  const [note, setNote] = useState('');
  if (!row) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 24, width: 480, maxWidth: '90vw',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>
          Mark as Fixed
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
          {row.open_po} — {row.supplier}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          Invoice {row.invoice_ref} · {fmtMoney(row.open_total)} · duplicate of {row.received_po}
        </div>

        <div style={{
          fontSize: 11, color: 'var(--muted)', background: 'var(--bg)',
          border: '1px solid var(--border2)', borderRadius: 4,
          padding: '8px 10px', marginBottom: 16, lineHeight: 1.5,
        }}>
          This removes the row from the worklist. It does not void anything —
          void {row.open_po} in Cin7 itself.
        </div>

        <textarea
          placeholder="Note (optional) — e.g. voided in Cin7, or not a duplicate because…"
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          style={{
            width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)',
            borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
            fontSize: 12, padding: '8px 10px', resize: 'vertical', marginBottom: 16,
            boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--muted)', marginRight: 'auto' }}>
            Recorded against {initials}
          </span>
          <button className="action-btn action-btn--ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="action-btn action-btn--close"
            disabled={saving}
            onClick={() => onConfirm(row, note)}
          >
            {saving ? 'Saving…' : 'Mark Fixed'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const DataSweep = () => {
  const [rows, setRows]                 = useState([]);
  const [staff, setStaff]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [tierFilter, setTierFilter]     = useState('all');
  const [search, setSearch]             = useState('');
  const [fixing, setFixing]             = useState(null);
  const [saving, setSaving]             = useState(false);

  // Remembered per browser, same as the Digital Metrics "Entering as" picker.
  const [initials, setInitialsState] = useState(
    () => window.localStorage.getItem('dataSweepInitials') || ''
  );
  const setInitials = (v) => {
    setInitialsState(v);
    if (v) window.localStorage.setItem('dataSweepInitials', v);
    else   window.localStorage.removeItem('dataSweepInitials');
  };

  const load = useCallback(async (withResolved) => {
    setLoading(true);
    setError(null);

    const [{ data: staffRows }, { data, error: rpcError }] = await Promise.all([
      supabase.from('staff').select('name, initials').order('initials'),
      supabase.rpc('get_duplicate_po_findings', { include_resolved: withResolved }),
    ]);

    if (rpcError) {
      // Say what actually failed. An empty table would read as "nothing wrong",
      // which is the opposite of the truth when the query itself did not run.
      setError(rpcError.message);
      setRows([]);
    } else {
      setRows(data || []);
    }

    setStaff(staffRows || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(showResolved); }, [load, showResolved]);

  const handleFixConfirm = async (row, note) => {
    setSaving(true);

    const { error: writeError } = await supabase
      .from('data_sweep_dismissals')
      .upsert({
        check_key:       CHECK_KEY,
        order_id:        row.open_order_id,
        order_ref:       row.open_po,
        invoice_ref:     row.invoice_ref,
        resolved_by:     initials,
        resolved_at:     new Date().toISOString(),
        resolution_note: note || null,
      }, { onConflict: 'check_key,order_id' });

    setSaving(false);

    if (writeError) {
      alert(`Could not mark ${row.open_po} as fixed: ${writeError.message}`);
      return;
    }

    setFixing(null);
    load(showResolved);
  };

  const handleReopen = async (row) => {
    const { error: delError } = await supabase
      .from('data_sweep_dismissals')
      .delete()
      .eq('check_key', CHECK_KEY)
      .eq('order_id', row.open_order_id);

    if (delError) {
      alert(`Could not reopen ${row.open_po}: ${delError.message}`);
      return;
    }
    load(showResolved);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tierFilter === 'exact' && r.match_type !== '100%') return false;
      if (tierFilter === 'close' && r.match_type !== 'Close Match') return false;
      if (!q) return true;
      return [r.supplier, r.invoice_ref, r.open_po, r.received_po]
        .join(' ').toLowerCase().includes(q);
    });
  }, [rows, tierFilter, search]);

  const openRows  = rows.filter(r => !r.resolved_at);
  const exact     = openRows.filter(r => r.match_type === '100%');
  const close     = openRows.filter(r => r.match_type === 'Close Match');
  const sumOf     = (list) => list.reduce((t, r) => t + Number(r.open_total || 0), 0);

  const tiers = [
    { key: 'all',   label: `All (${openRows.length})` },
    { key: 'exact', label: `100% Match (${exact.length})` },
    { key: 'close', label: `Close Match (${close.length})` },
  ];

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">

          <div className="dash-panel__header">
            <span className="dash-panel__title">
              🧹 Data Sweep — Duplicate PO Invoice Refs
              {exact.length > 0 && (
                <span style={{
                  marginLeft: 8, background: '#f87171', color: '#fff',
                  borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {exact.length} to void
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>Entering as</span>
              <select
                value={initials}
                onChange={e => setInitials(e.target.value)}
                style={{
                  background: 'var(--bg)',
                  // Outlined amber while empty: the Mark Fixed buttons are
                  // disabled until someone is chosen, and a disabled button
                  // with no visible cause just reads as broken.
                  border: `1px solid ${initials ? 'var(--border2)' : 'var(--gold)'}`,
                  borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
                  fontSize: 11, padding: '3px 6px', outline: 'none',
                }}
              >
                <option value="">— select —</option>
                {staff.map(s => (
                  <option key={s.initials} value={s.initials}>
                    {s.name} ({s.initials})
                  </option>
                ))}
              </select>
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setShowResolved(v => !v)}
              >
                {showResolved ? 'Hide Fixed' : 'Show Fixed'}
              </button>
              {/* This page is live, but the Operational Events row on the
                  dashboard shows the LAST RUN's verdict. Marking everything
                  fixed here empties the list without turning that row green
                  until the sweep runs again -- so it stays red for hours with
                  nothing actually outstanding. Same reason Product Error
                  Management carries this button. */}
              <RefreshButton
                triggerKey="data_sweep"
                label="Re-run Check"
                onDone={() => load(showResolved)}
              />
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => load(showResolved)}
                disabled={loading}
              >
                ↻ Refresh
              </button>
            </div>
          </div>

          {/* What the two tiers mean. Without this the difference between a
              100% row and a Close Match row is invisible on screen. */}
          <div style={{
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
            fontSize: 11, color: 'var(--muted)', lineHeight: 1.6,
          }}>
            Open POs raised against a supplier invoice that has already been received on
            another PO. <strong style={{ color: 'var(--white)' }}>100% Match</strong> — total
            agrees to the cent and nothing has been received against the open PO; safe to void
            in Cin7. <strong style={{ color: 'var(--white)' }}>Close Match</strong> — same
            invoice reference but the total differs, or stock has already been received; check
            before doing anything.
          </div>

          {!initials && (
            <div style={{
              padding: '8px 16px', borderBottom: '1px solid var(--border)',
              fontSize: 11, color: 'var(--gold)',
            }}>
              Choose who you are entering as, top right, to enable the Mark Fixed
              buttons — every entry is recorded against a person.
            </div>
          )}

          <div style={{
            display: 'flex', gap: 16, padding: '10px 16px',
            borderBottom: '1px solid var(--border)', fontSize: 11,
          }}>
            <span style={{ color: 'var(--muted)' }}>
              100% Match:{' '}
              <strong style={{ color: 'var(--white)' }}>
                {exact.length} PO{exact.length === 1 ? '' : 's'} · {fmtMoney(sumOf(exact))}
              </strong>
            </span>
            <span style={{ color: 'var(--muted)' }}>
              Close Match:{' '}
              <strong style={{ color: 'var(--white)' }}>
                {close.length} PO{close.length === 1 ? '' : 's'} · {fmtMoney(sumOf(close))}
              </strong>
            </span>
          </div>

          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
          }}>
            {tiers.map(t => (
              <button
                key={t.key}
                className={`action-btn ${tierFilter === t.key ? 'action-btn--active' : 'action-btn--ghost'}`}
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setTierFilter(t.key)}
              >
                {t.label}
              </button>
            ))}
            <input
              type="text"
              placeholder="Search supplier, invoice ref or PO…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)',
                borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
                fontSize: 12, padding: '5px 10px', outline: 'none',
              }}
            />
          </div>

          {error ? (
            <div className="dash-empty" style={{ color: '#f87171' }}>
              Could not load findings: {error}
            </div>
          ) : loading ? (
            <div className="dash-loading">Loading…</div>
          ) : !filtered.length ? (
            <div className="dash-empty dash-empty--ok">
              {rows.length ? '✓ No rows match this filter' : '✓ No duplicate invoice references'}
            </div>
          ) : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Match</th>
                  <th>Supplier</th>
                  <th>Invoice Ref</th>
                  <th>Open PO</th>
                  <th style={{ textAlign: 'right' }}>Open Total</th>
                  <th>Received PO</th>
                  <th style={{ textAlign: 'right' }}>Received Total</th>
                  <th style={{ textAlign: 'right' }}>Variance</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const isExact    = row.match_type === '100%';
                  const isResolved = !!row.resolved_at;
                  return (
                    <tr
                      key={row.open_order_id}
                      className={!isResolved && isExact ? 'dash-row--action' : ''}
                      style={isResolved ? { opacity: 0.55 } : undefined}
                    >
                      <td>
                        <span style={{
                          fontSize: 10, fontWeight: 700, borderRadius: 10,
                          padding: '1px 8px', whiteSpace: 'nowrap',
                          background: isExact ? '#f87171' : 'var(--border2)',
                          color:      isExact ? '#fff'    : 'var(--white)',
                        }}>
                          {row.match_type}
                        </span>
                      </td>
                      <td>{row.supplier}</td>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>{row.invoice_ref}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                        {row.open_po}
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                          {row.open_stage} · {row.open_age_days}d old
                          {Number(row.open_qty_received) > 0 && (
                            <span style={{ color: 'var(--gold)' }}>
                              {' '}· {row.open_qty_received} received
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {fmtMoney(row.open_total)}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                        {row.received_po}
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                          {row.received_created}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {fmtMoney(row.received_total)}
                      </td>
                      <td style={{
                        textAlign: 'right', whiteSpace: 'nowrap',
                        color: Number(row.variance) === 0 ? 'var(--muted)' : 'var(--gold)',
                      }}>
                        {Number(row.variance) === 0 ? '—' : fmtMoney(row.variance)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isResolved ? (
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--green-light)' }}>
                              ✓ Fixed{row.resolved_by ? ` — ${row.resolved_by}` : ''}
                            </div>
                            <div style={{ fontSize: 9, color: 'var(--muted)' }}>
                              {fmtNZT(row.resolved_at)}
                            </div>
                            {row.resolution_note && (
                              <div style={{
                                fontSize: 10, color: 'var(--gold)',
                                fontStyle: 'italic', marginTop: 2,
                              }}>
                                {row.resolution_note}
                              </div>
                            )}
                            <button
                              className="action-btn action-btn--ghost"
                              style={{ fontSize: 10, padding: '2px 10px', marginTop: 4 }}
                              onClick={() => handleReopen(row)}
                            >
                              Reopen
                            </button>
                          </div>
                        ) : (
                          <button
                            className="action-btn action-btn--ghost"
                            style={{ fontSize: 10, padding: '2px 10px' }}
                            disabled={!initials}
                            title={initials
                              ? 'Remove this row from the worklist'
                              : 'Choose who you are entering as first'}
                            onClick={() => setFixing(row)}
                          >
                            Mark Fixed
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <FixModal
        row={fixing}
        initials={initials}
        saving={saving}
        onConfirm={handleFixConfirm}
        onCancel={() => setFixing(null)}
      />
    </main>
  );
};

export default DataSweep;
